import { ResponsiveKpis } from '@/components/responsive-kpis';
import { PageActions } from '@/components/page-actions';
import { ResponsiveFilters } from '@/components/responsive-filters';
import { useCallback, useId, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from '@tanstack/react-router';

import {
  useCampaigns,
  useCampaignOverview,
  useCampaignOverviewExport,
  useProductions,
} from '@/api/hooks';
import type {
  CampaignOverviewKpis,
  CampaignOverviewPacing,
  CampaignOverviewResponse,
  CampaignOverviewRow,
  CampaignOverviewSortDirection,
} from '@/api/types';
import { useCan } from '@/auth/capabilities';
import { performanceMetricCell } from '@/components/performance-metric-cell';
import { createDataTable } from '@/components/data-table';
import {
  DateFilter,
  Dropdown,
  SearchableDropdown,
} from '@/components/dropdowns';
import { MultiSelect } from '@/components/multi-select';
import type { MultiSelectOption } from '@/components/multi-select';
import { ChannelPill, Icons, MoneyCell, StatusDot } from '@/components/primitives';
import { productionDisplayName } from '@/lib/production-name';
import { ReportingDataControls } from '@/components/reporting-controls';
import {
  currentDayTtdLaneNote,
  reportMetricIsAvailable,
  reportingMetricAvailability,
} from '@/lib/reporting-availability';
import { buildCampaignOverviewRequest } from '@/lib/campaigns-overview-request';
import {
  CAMPAIGNS_OVERVIEW_AUDIENCE_TYPES,
} from '@/lib/campaigns-overview-search';
import type {
  CampaignsOverviewAudienceType,
  CampaignsOverviewSearch,
  CampaignsOverviewSearchPatch,
  CampaignsOverviewSortField,
} from '@/lib/campaigns-overview-search';
import { campaignListStatusLabel, campaignStatusKind } from '@/lib/campaign-status';
import { csvFilename, triggerDownload } from '@/lib/csv';
import {
  fmtDateInput,
  fmtDateTime,
  fmtDay,
  nyDateInputToMs,
  todayNyInput, fmtDayShort } from '@/lib/datetime';
import type {
  DirectPerformanceMetrics,
  PerformanceMetricId,
} from '@/lib/performance-metrics';
import {
  parsePerformanceReportSearch,
  serializePerformanceReportSearch,
} from '@/lib/performance-report-search';

import './dashboard.css';

interface DashboardProps {
  search: CampaignsOverviewSearch;
  onSearchChange: (
    patch: CampaignsOverviewSearchPatch,
    options?: { replace?: boolean },
  ) => void;
}

type DashboardRow = CampaignOverviewRow & { id: string };

const CHANNEL_OPTIONS = [
  { id: 'all', label: 'All channels' },
  { id: 'meta', label: 'Meta', swatch: 'oklch(0.60 0.18 250)' },
  { id: 'ctv', label: 'CTV', swatch: 'oklch(0.60 0.18 320)' },
  { id: 'dsp', label: 'Display', swatch: 'oklch(0.60 0.16 145)' },
];

/**
 * Ended sits third, beside the states a person filters for most: the menu is
 * 360 px tall (fewer rows on touch), and as the last of fourteen it was below
 * the fold, where nobody looking for finished campaigns found it (2026-10-19).
 * Same order as `CAMPAIGNS_OVERVIEW_STATUSES`; the DOM test holds the two equal.
 */
export const CAMPAIGN_STATUS_OPTIONS = [
  { id: 'all', label: 'All statuses' },
  { id: 'live', label: 'Live', dot: 'var(--live)' },
  { id: 'ended', label: 'Ended', dot: 'var(--ink-2)' },
  { id: 'paused', label: 'Paused', dot: 'var(--ink-3)' },
  { id: 'scheduled', label: 'Scheduled', dot: 'var(--warn)' },
  { id: 'needs_attention', label: 'Needs attention', dot: 'var(--warn)' },
  { id: 'unverified', label: 'Status unverified', dot: 'var(--warn)' },
  { id: 'paused_by_system', label: 'Paused by system', dot: 'var(--warn)' },
  { id: 'pausing', label: 'Pausing', dot: 'var(--warn)' },
  { id: 'starting', label: 'Starting / In review', dot: 'var(--warn)' },
  { id: 'partially_active', label: 'Partially active', dot: 'var(--warn)' },
  { id: 'armed', label: 'Armed', dot: 'var(--accent)' },
  { id: 'draft', label: 'Draft', dot: 'var(--warn)' },
  { id: 'ready_to_publish', label: 'Ready to publish', dot: 'var(--good)' },
];

const AUDIENCE_TYPE_OPTIONS: MultiSelectOption<CampaignsOverviewAudienceType>[] = [
  { id: 'direct', label: 'Direct' },
  { id: 'lookalike', label: 'LAL' },
  { id: 'retargeting', label: 'Remarketing' },
];

const AUDIENCE_TYPE_LABELS: Record<CampaignsOverviewAudienceType, string> = {
  direct: 'Direct',
  lookalike: 'LAL',
  retargeting: 'Remarketing',
};

const PACING_LABELS: Record<CampaignOverviewPacing['status'], string> = {
  behind: 'Behind',
  on_pace: 'On pace',
  ahead: 'Ahead',
  not_started: 'Not started',
  not_launched: 'Not launched',
  paused: 'Paused',
  ended: 'Ended',
  unavailable: 'Unavailable',
};

const ASCENDING_FIRST = new Set<CampaignsOverviewSortField>([
  'campaign',
  'production',
  'channel',
  'status',
  'audience_type',
  'start_date',
  'end_date',
  'pacing',
]);

function number(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/**
 * The direct-sales primitives `/reports` → Performance computes its figures
 * from. The overview carries the same primitives (the server reads them with
 * the Reports query), so Tickets, Ticket Value, CPA and ROAS here are the
 * Reports formulas over the Reports numbers (`performanceMetricValue`).
 */
type OverviewSales = Pick<
  CampaignOverviewKpis,
  'gross_spend_cents' | 'attribution_gross_spend_cents' | 'tickets_micros' | 'ticket_value_micros'
> & {
  impressions?: number | null;
  clicks?: number | null;
};

function directMetrics(source: OverviewSales): DirectPerformanceMetrics {
  return {
    gross_spend_cents: number(source.gross_spend_cents),
    attribution_gross_spend_cents: source.attribution_gross_spend_cents == null
      ? null
      : number(source.attribution_gross_spend_cents),
    impressions: number(source.impressions),
    clicks: number(source.clicks),
    attributed_conversions_micros: number(source.tickets_micros),
    attributed_revenue_micros: number(source.ticket_value_micros),
  };
}

/** Why a sales figure is withheld, or `undefined` when it can be shown. */
function salesReason(
  source: OverviewSales,
  integrityError: string | null,
  attributionAvailable: boolean,
): string | undefined {
  return integrityError
    ?? (!attributionAvailable
      || source.tickets_micros == null
      || source.ticket_value_micros == null
      ? 'Attribution unavailable'
      : undefined);
}

/**
 * Why CPA and ROAS are withheld. Their denominator is delivery spend on the
 * attributed scopes, so Reports shows them only when both delivery and
 * attribution are published (`reportingMetricAvailability`'s `roas`).
 */
function ratioReason(
  source: OverviewSales,
  integrityError: string | null,
  ratiosAvailable: boolean,
): string | undefined {
  return integrityError
    ?? (!ratiosAvailable
      || source.attribution_gross_spend_cents == null
      || source.tickets_micros == null
      || source.ticket_value_micros == null
      ? 'CPA and ROAS unavailable'
      : undefined);
}

function salesCell(
  metrics: DirectPerformanceMetrics,
  metric: PerformanceMetricId,
  reason: string | undefined,
): ReactNode {
  return reason
    ? <span className="dim" title={reason}>—</span>
    : performanceMetricCell(metrics, metric);
}

/**
 * Fail closed unless the server stamped these figures direct-only: the
 * Campaigns page shows sales of each campaign's own show and nothing else.
 */
export function campaignOverviewIntegrityError(
  response: CampaignOverviewResponse | undefined,
): string | null {
  if (!response) return null;
  if (response.methodology.attribution_scope !== 'direct_only') {
    return 'Campaign figures could not be verified as sales of each campaign\'s own show.';
  }
  return null;
}

/** The same campaign and dates in Reports → Performance, By Campaign. */
function reportsSearchFor(campaignId: string, from: string, to: string) {
  return serializePerformanceReportSearch(parsePerformanceReportSearch({
    tab: 'campaign',
    from,
    to,
    campaignId,
  }));
}

/** `$45k of $135.5k planned` — the KPI strip has one line for this; the exact figures live in the tooltip. */
function compactMoney(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 }).format(cents / 100);
}

function money(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function signedMoney(cents: number): string {
  const formatted = money(Math.abs(cents));
  return cents > 0 ? `+${formatted}` : cents < 0 ? `−${formatted}` : formatted;
}

function productionSwatch(id: string): string {
  let hash = 0;
  for (const char of id) hash = ((hash * 31) + char.charCodeAt(0)) | 0;
  return `oklch(42% 0.12 ${Math.abs(hash) % 360})`;
}

export function PacingCell({ pacing }: { pacing: CampaignOverviewPacing }) {
  const tooltipId = useId();
  const [tooltip, setTooltip] = useState<{ left: number; top: number } | null>(null);
  const ratio = pacing.ratio == null ? null : number(pacing.ratio);
  const label = PACING_LABELS[pacing.status];
  // A finished campaign's figure is final only once reporting covers its last
  // day; until then the server sends no ratio, and the cell says the spend is
  // pending rather than a bare "Ended" that reads as a final $0 (2026-10-19).
  const spendPending = pacing.status === 'ended' && ratio == null;
  const heading = spendPending ? 'Ended · spend pending' : label;
  const details = [
    pacing.actual_gross_spend_cents == null
      ? null
      : `Actual ${money(pacing.actual_gross_spend_cents)}`,
    pacing.planned_spend_cents == null
      ? null
      : `Planned ${money(pacing.planned_spend_cents)}`,
    pacing.variance_cents == null ? null : `Variance ${signedMoney(pacing.variance_cents)}`,
    pacing.cutoff ? `through ${fmtDay(pacing.cutoff)}` : null,
  ].filter(Boolean).join(' · ');
  const primary = pacing.status === 'unavailable'
    ? '—'
    : ratio == null ? label : `${(ratio * 100).toFixed(0)}%`;
  const secondary = spendPending ? 'spend pending' : ratio != null ? label : null;
  const activate = (
    target: HTMLButtonElement,
    clientX?: number,
    clientY?: number,
  ) => {
    const rect = target.getBoundingClientRect();
    setTooltip({
      left: clientX ?? rect.left + rect.width / 2,
      top: clientY ?? rect.top,
    });
  };
  return (
    <>
      <button
        type="button"
        className={`campaign-pacing campaign-pacing--${pacing.status}`}
        aria-describedby={tooltip ? tooltipId : undefined}
        aria-label={`${spendPending ? 'Ended, spend pending' : `${label} pacing`}${ratio == null ? '' : `, ${(ratio * 100).toFixed(0)} percent`}${details ? `. ${details}` : ''}`}
        onFocus={(event) => activate(event.currentTarget)}
        onBlur={() => setTooltip(null)}
        onPointerEnter={(event) => activate(
          event.currentTarget,
          event.clientX,
          event.clientY,
        )}
        onPointerMove={(event) => activate(
          event.currentTarget,
          event.clientX,
          event.clientY,
        )}
        onPointerDown={(event) => {
          event.stopPropagation();
          activate(event.currentTarget, event.clientX, event.clientY);
        }}
        onPointerLeave={() => setTooltip(null)}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <strong>{primary}</strong>
        {secondary && <small>{secondary}</small>}
      </button>
      {tooltip && typeof document !== 'undefined' && createPortal(
        <span
          id={tooltipId}
          role="tooltip"
          className="campaign-pacing-tooltip"
          style={{ left: tooltip.left, top: tooltip.top }}
        >
          <strong>{heading}{ratio == null ? '' : ` · ${(ratio * 100).toFixed(0)}%`}</strong>
          {pacing.actual_gross_spend_cents != null && (
            <span>Actual <b>{money(pacing.actual_gross_spend_cents)}</b></span>
          )}
          {pacing.planned_spend_cents != null && (
            <span>Planned <b>{money(pacing.planned_spend_cents)}</b></span>
          )}
          {pacing.variance_cents != null && (
            <span>Variance <b>{signedMoney(pacing.variance_cents)}</b></span>
          )}
          {pacing.cutoff && <em>Through {fmtDay(pacing.cutoff)} ET</em>}
        </span>,
        document.body,
      )}
    </>
  );
}

function AudienceTypeChips({ types }: { types: CampaignOverviewRow['audience_types'] }) {
  if (!types.length) return <span className="dim">—</span>;
  return (
    <div className="campaign-audience-types">
      {CAMPAIGNS_OVERVIEW_AUDIENCE_TYPES.filter((type) => types.includes(type)).map((type) => (
        <span key={type} className={`campaign-audience-type campaign-audience-type--${type}`}>
          {AUDIENCE_TYPE_LABELS[type]}
        </span>
      ))}
    </div>
  );
}

export function CampaignKpiStrip({
  kpis,
  integrityError,
  deliveryCutoff,
  attributionAvailable = true,
  ratiosAvailable = attributionAvailable,
}: {
  kpis: CampaignOverviewKpis | undefined;
  integrityError: string | null;
  deliveryCutoff?: string | null;
  attributionAvailable?: boolean;
  ratiosAvailable?: boolean;
}) {
  const sales: OverviewSales = kpis ?? {
    gross_spend_cents: null,
    attribution_gross_spend_cents: null,
    tickets_micros: null,
    ticket_value_micros: null,
  };
  const metrics = directMetrics(sales);
  const pacing = kpis?.pacing;
  const reason = kpis ? salesReason(sales, integrityError, attributionAvailable) : 'Attribution unavailable';
  const ratios = kpis ? ratioReason(sales, integrityError, ratiosAvailable) : 'CPA and ROAS unavailable';
  return (
    <ResponsiveKpis><div className="kpi-strip campaigns-kpis">
      <div className="kpi">
        <div className="kpi-label">Budget</div>
        <div className="kpi-val">{kpis ? <MoneyCell value={kpis.budget_cents / 100} dp={2}/> : '—'}</div>
        <div className="kpi-delta"><span className="lbl">Full campaign budgets</span></div>
      </div>
      <div className="kpi">
        <div className="kpi-label">Gross spend</div>
        <div className="kpi-val">
          {kpis?.gross_spend_cents == null
            ? <span title="Delivery unavailable">—</span>
            : <MoneyCell value={kpis.gross_spend_cents / 100} dp={2}/>}
        </div>
        <div className="kpi-delta"><span className="lbl">Selected dates</span></div>
      </div>
      <div className="kpi">
        <div className="kpi-label">Pacing</div>
        <div className="kpi-val">
          {pacing?.ratio == null ? '—' : `${(pacing.ratio * 100).toFixed(0)}%`}
        </div>
        <div className="kpi-delta">
          <span
            className="lbl"
            title={pacing?.actual_gross_spend_cents == null || pacing.planned_spend_cents == null
              ? undefined
              : `${money(pacing.actual_gross_spend_cents)} actual · ${money(pacing.planned_spend_cents)} planned${deliveryCutoff ? ` · through ${fmtDay(deliveryCutoff)}` : ''}`}
          >
            {pacing?.actual_gross_spend_cents == null || pacing.planned_spend_cents == null
              ? 'Current pacing unavailable'
              : `${compactMoney(pacing.actual_gross_spend_cents)} of ${compactMoney(pacing.planned_spend_cents)} planned`}
          </span>
        </div>
      </div>
      <div className="kpi">
        <div className="kpi-label">Tickets</div>
        <div className="kpi-val">{salesCell(metrics, 'attributed_conversions', reason)}</div>
      </div>
      <div className="kpi">
        <div className="kpi-label">Ticket Value</div>
        <div className="kpi-val">{salesCell(metrics, 'attributed_revenue', reason)}</div>
      </div>
      <div className="kpi">
        <div className="kpi-label">CPA</div>
        <div className="kpi-val">{salesCell(metrics, 'cpa', ratios)}</div>
      </div>
      <div className="kpi">
        <div className="kpi-label">ROAS</div>
        <div className="kpi-val">{salesCell(metrics, 'roas', ratios)}</div>
      </div>
    </div></ResponsiveKpis>
  );
}

function FilterBar({
  search,
  campaigns,
  productions,
  onSearchChange,
}: {
  search: CampaignsOverviewSearch;
  campaigns: Array<{ id: string; name: string }>;
  productions: Array<{ id: string; name: string }>;
  onSearchChange: DashboardProps['onSearchChange'];
}) {
  return (
    <ResponsiveFilters className="campaigns-filter-bar">
      <div className={`campaigns-filter-field${search.campaignId ? '' : ' is-default-selection'}`}>
        <span>CAMPAIGN</span>
        <SearchableDropdown
          value={search.campaignId ?? ''}
          options={campaigns.map((campaign) => ({
            id: campaign.id,
            label: campaign.name,
          }))}
          onChange={(campaignId: string) => onSearchChange({
            campaignId: campaignId || undefined,
          })}
          emptyOptionLabel="All campaigns"
          emptyOptionValue=""
          placeholder="All campaigns"
          searchPlaceholder="Search campaigns…"
          width="100%"
          size="sm"
        />
      </div>
      <div className={`campaigns-filter-field${search.channel ? '' : ' is-default-selection'}`}>
        <Dropdown
          label="CHANNEL"
          value={search.channel ?? 'all'}
          options={CHANNEL_OPTIONS}
          onChange={(value: string) => onSearchChange({
            channel: value === 'all' ? undefined : value as CampaignsOverviewSearch['channel'],
          })}
        />
      </div>
      <div className={`campaigns-filter-field${search.status ? '' : ' is-default-selection'}`}>
        <Dropdown
          label="STATUS"
          value={search.status ?? 'all'}
          options={CAMPAIGN_STATUS_OPTIONS}
          onChange={(value: string) => onSearchChange({
            status: value === 'all' ? undefined : value as CampaignsOverviewSearch['status'],
          })}
        />
      </div>
      <div className={`campaigns-filter-field${search.productionId ? '' : ' is-default-selection'}`}>
        <span>PRODUCTION</span>
        <SearchableDropdown
          value={search.productionId ?? ''}
          options={productions.map((production) => ({
            id: production.id,
            label: production.name,
          }))}
          onChange={(productionId: string) => onSearchChange({
            productionId: productionId || undefined,
          })}
          emptyOptionLabel="All productions"
          emptyOptionValue=""
          placeholder="All productions"
          searchPlaceholder="Search Production"
        />
      </div>
      <div className={`campaigns-filter-field${search.audienceTypes.length ? '' : ' is-default-selection'}`}>
        <span>AUDIENCE TYPE</span>
        <MultiSelect
          value={search.audienceTypes}
          options={AUDIENCE_TYPE_OPTIONS}
          order="ranked"
          onChange={(audienceTypes: CampaignsOverviewAudienceType[]) => onSearchChange({
            audienceTypes,
          })}
          placeholder="All audience types"
          searchPlaceholder="Search audience type"
        />
      </div>
    </ResponsiveFilters>
  );
}

function ariaSort(
  field: CampaignsOverviewSortField,
  search: CampaignsOverviewSearch,
): 'ascending' | 'descending' | undefined {
  if (search.sortField !== field) return undefined;
  return search.sortDirection === 'asc' ? 'ascending' : 'descending';
}

function SortHeader({
  label,
  field,
  search,
  onSort,
}: {
  label: string;
  field: CampaignsOverviewSortField;
  search: CampaignsOverviewSearch;
  onSort: (field: CampaignsOverviewSortField) => void;
}) {
  const active = search.sortField === field;
  return (
    <button
      type="button"
      className={`campaign-sort-header${active ? ' is-active' : ''}`}
      onClick={() => onSort(field)}
    >
      <span>{label}</span>
      <span aria-hidden="true">
        {active ? (search.sortDirection === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </button>
  );
}

function tableFactory(
  search: CampaignsOverviewSearch,
  onSort: (field: CampaignsOverviewSortField) => void,
) {
  const sortable = (key: CampaignsOverviewSortField, label: string) => ({
    header: <SortHeader label={label} field={key} search={search} onSort={onSort}/>,
    ariaSort: ariaSort(key, search),
  });
  // Keep pinned offsets fixed; numeric media widths become proportional
  // minmax tracks that retain these floors and absorb all remaining space.
  // Status is fixed at its longest label ("starting / in review", 121 px
  // measured 2026-10-22) plus the cell's padding, so it never takes a share.
  return createDataTable([
    { key: 'campaign', ...sortable('campaign', 'Campaign'), width: '338px', sticky: true, mobile: 'title' },
    { key: 'production', ...sortable('production', 'Production'), width: '150px', sticky: true, mobile: 'subtitle' },
    { key: 'channel', ...sortable('channel', 'Channel'), width: 96, priority: 2, mobile: 'stat' },
    { key: 'status', ...sortable('status', 'Status'), width: '148px', mobile: 'status' },
    { key: 'audience', ...sortable('audience_type', 'Audience Type'), width: 132, priority: 3 },
    { key: 'pacing', ...sortable('pacing', 'Pacing'), width: 92, mobile: 'stat' },
    { key: 'start', ...sortable('start_date', 'Start Date'), width: 78, priority: 3, mobile: { role: 'stat', label: 'Flight', order: 4 } },
    { key: 'end', ...sortable('end_date', 'End Date'), width: 78, priority: 3, mobile: { role: 'stat', join: { with: 'start', separator: ' → ' } } },
    { key: 'budget', ...sortable('budget', 'Budget'), width: 82, align: 'num' as const, mobile: { role: 'stat', label: 'Spend / budget' } },
    { key: 'spend', ...sortable('gross_spend', 'Gross spend'), width: 86, align: 'num' as const, mobile: { role: 'stat', join: { with: 'budget', separator: ' / ', order: 'before' } } },
    { key: 'roas', ...sortable('roas', 'ROAS'), width: 76, align: 'num' as const, priority: 2, mobile: { role: 'stat', label: 'ROAS', order: 6 } },
    { key: 'cpa', ...sortable('cpa', 'CPA'), width: 84, align: 'num' as const, priority: 3 },
    { key: 'tickets', ...sortable('tickets', 'Tickets'), width: 92, align: 'num' as const, priority: 2, mobile: { role: 'stat', label: 'Tickets', order: 5 } },
    { key: 'value', ...sortable('ticket_value', 'Ticket Value'), width: 108, align: 'num' as const, priority: 3 },
    { key: 'impressions', ...sortable('impressions', 'Impressions'), width: 82, align: 'num' as const, priority: 3 },
    { key: 'cpm', ...sortable('cpm', 'CPM'), width: 68, align: 'num' as const, priority: 3 },
  ]);
}

export function Dashboard({ search, onSearchChange }: DashboardProps) {
  const canExport = useCan('reports_export');
  const request = useMemo(() => buildCampaignOverviewRequest(search), [search]);
  const overviewQuery = useCampaignOverview(request);
  const campaignsQuery = useCampaigns();
  const productionsQuery = useProductions();
  const exportMutation = useCampaignOverviewExport();
  const response = overviewQuery.data;
  const integrityError = campaignOverviewIntegrityError(response);
  const rows = useMemo<DashboardRow[]>(
    () => (response?.rows ?? []).map((row) => ({ ...row, id: row.campaign_id })),
    [response?.rows],
  );
  const onSort = useCallback((field: CampaignsOverviewSortField) => {
    const direction: CampaignOverviewSortDirection = search.sortField === field
      ? search.sortDirection === 'asc' ? 'desc' : 'asc'
      : ASCENDING_FIRST.has(field) ? 'asc' : 'desc';
    onSearchChange({ sortField: field, sortDirection: direction });
  }, [onSearchChange, search.sortDirection, search.sortField]);
  const table = useMemo(
    () => tableFactory(search, onSort),
    [onSort, search],
  );
  const { Table, sticky } = table;
  const dateValue = useMemo(() => ({
    preset: 'custom',
    start: new Date(nyDateInputToMs(search.from)),
    end: new Date(nyDateInputToMs(search.to)),
  }), [search.from, search.to]);
  const hasActiveFilters = !!(
    search.campaignId
    || search.channel
    || search.status
    || search.productionId
    || search.audienceTypes.length
  );
  const exportUnavailable = response?.kpis.gross_spend_cents == null;
  const availability = reportingMetricAvailability('performance', response?.freshness);
  const attributionAvailable = reportMetricIsAvailable(availability, 'attribution');
  const ratiosAvailable = reportMetricIsAvailable(availability, 'roas');
  const todayNy = todayNyInput();
  const includesToday = search.from <= todayNy && todayNy <= search.to;
  const ttdLaneNote = currentDayTtdLaneNote(response?.freshness);
  const exportDisabled = !canExport
    || rows.length === 0
    || !!integrityError
    || exportUnavailable
    || overviewQuery.isPlaceholderData
    || exportMutation.isPending;
  const exportCsv = async () => {
    if (exportDisabled) return;
    const csv = await exportMutation.mutateAsync(request);
    triggerDownload(
      csvFilename('campaigns-direct'),
      new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8' }),
    );
  };

  return (
    <div className="dash-frame campaigns-overview">
      <div className="page-hd campaigns-page-heading">
        <h1 className="page-title">Campaigns</h1>
        <PageActions>
          <ReportingDataControls
            data-phone="meta"
            freshness={response?.freshness}
            dataset="performance"
            highlightAge
          />
          <button
            className="btn"
            disabled={exportDisabled}
            onClick={() => void exportCsv()}
            title={!canExport
              ? 'You do not have permission to export reports'
              : integrityError
                ?? (exportUnavailable
                  ? 'Export is disabled while delivery facts are unavailable.'
                  : 'Export the active filtered and sorted campaign view')}
          >
            <span aria-hidden="true">{Icons.dl}</span>
            {exportMutation.isPending ? 'Exporting…' : 'Export CSV'}
          </button>
          <Link
            className="btn btn-primary"
            data-phone="inline"
            to="/campaigns/new"
            preload="intent"
          >
            <span aria-hidden="true">{Icons.plus}</span>
            <span className="phone-long">New Campaign</span><span className="phone-short">New</span>
          </Link>
          <DateFilter
            completedDaysOnly
            value={dateValue}
            onChange={(date: { start: Date; end: Date }) => onSearchChange({
              from: fmtDateInput(date.start),
              to: fmtDateInput(date.end),
            })}
          />
        </PageActions>
      </div>

      <CampaignKpiStrip
        kpis={response?.kpis}
        integrityError={integrityError}
        deliveryCutoff={response?.delivery_cutoff}
        attributionAvailable={attributionAvailable}
        ratiosAvailable={ratiosAvailable}
      />
      {includesToday && (
        <div className="campaigns-attribution-cutoff" role="status">
          Today is included. Current-day delivery is provisional; Tickets, Ticket Value, CPA, and ROAS use completed ET days only.
          {ttdLaneNote && <> {ttdLaneNote}</>}
        </div>
      )}
      {integrityError && (
        <div className="campaigns-integrity-error" role="alert">
          <strong>Attribution integrity check failed.</strong> {integrityError}
        </div>
      )}
      {exportMutation.isError && (
        <div className="campaigns-export-error" role="alert">
          CSV export failed. Please try again.
        </div>
      )}
      <FilterBar
        search={search}
        campaigns={campaignsQuery.data ?? []}
        productions={productionsQuery.data ?? []}
        onSearchChange={onSearchChange}
      />

      <Table
        rows={rows}
        rowH={58}
        mobileStats={6}
        unfilteredTotal={rows.length}
        deps={[{ what: 'campaign overview', query: overviewQuery }]}
        loading="Loading campaigns…"
        empty={hasActiveFilters ? 'No campaigns match these filters.' : 'No campaigns yet.'}
        className="campaigns-table"
        // Muted by what the row says, not the stored status: a finished
        // campaign is still stored `live` and reads Ended (2026-10-19).
        rowClassName={(row) => {
          const kind = campaignStatusKind(row.status, row.auto_launch_requested_at, row.operational_status);
          return kind === 'ended' || kind === 'draft' ? 'row-mute' : undefined;
        }}
        renderRow={(row) => {
          const deliveryReason = row.performance.gross_spend_cents == null
            ? 'Delivery unavailable'
            : undefined;
          const attributionReason = salesReason(row.performance, integrityError, attributionAvailable);
          const ratiosReason = ratioReason(row.performance, integrityError, ratiosAvailable);
          const metrics = directMetrics(row.performance);
          const statusKind = campaignStatusKind(row.status, row.auto_launch_requested_at, row.operational_status);
          return (
            <>
              <td {...sticky(0, 'name')} title={row.campaign_name}>
                <div className="campaign-name-cell">
                  <Link
                    to="/campaigns/$campaignId"
                    params={{ campaignId: row.campaign_id }}
                    preload="intent"
                  >
                    {row.campaign_name}
                  </Link>
                  <Link
                    className="campaign-reports-link"
                    to="/reports"
                    search={reportsSearchFor(row.campaign_id, response?.performance_from ?? search.from, response?.performance_to ?? search.to)}
                    preload="intent"
                    title="Open this campaign's figures in Reports"
                    aria-label={`Open ${row.campaign_name} in Reports`}
                  >
                    Reports
                  </Link>
                </div>
              </td>
              <td {...sticky(1)} title={productionDisplayName(row.production_name)}>
                <div className="campaign-production-cell">
                  <span style={{ background: productionSwatch(row.production_id) }}/>
                  <strong>{productionDisplayName(row.production_name)}</strong>
                </div>
              </td>
              <td><ChannelPill channel={row.channel}/></td>
              {/* The arming stamp outlives the launch by one write, so it
                  is only the tooltip while the row still reads Armed. */}
              <td title={statusKind === 'armed' && row.auto_launch_requested_at
                ? `Armed ${fmtDateTime(row.auto_launch_requested_at)} ET`
                : campaignListStatusLabel(row.status, statusKind)}>
                <StatusDot s={statusKind} label={campaignListStatusLabel(row.status, statusKind)}/>
              </td>
              <td><AudienceTypeChips types={row.audience_types}/></td>
              <td><PacingCell pacing={row.pacing}/></td>
              <td><span className="phone-long">{fmtDay(row.start_date)}</span><span className="phone-short">{fmtDayShort(row.start_date)}</span></td>
              <td><span className="phone-long">{fmtDay(row.end_date)}</span><span className="phone-short">{fmtDayShort(row.end_date)}</span></td>
              <td className="num"><MoneyCell value={row.budget_cents / 100}/></td>
              <td className="num">{salesCell(metrics, 'gross_spend', deliveryReason)}</td>
              <td className="num">{salesCell(metrics, 'roas', ratiosReason)}</td>
              <td className="num">{salesCell(metrics, 'cpa', ratiosReason)}</td>
              <td className="num">{salesCell(metrics, 'attributed_conversions', attributionReason)}</td>
              <td className="num">{salesCell(metrics, 'attributed_revenue', attributionReason)}</td>
              <td className="num">{salesCell(metrics, 'impressions', deliveryReason)}</td>
              <td className="num">{salesCell(metrics, 'cpm', deliveryReason)}</td>
            </>
          );
        }}
      />
    </div>
  );
}

export type { DashboardProps };
